import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import toast, { Toaster } from 'react-hot-toast';
import { RulerHorizontalIcon, UpdateIcon } from '@radix-ui/react-icons';
import { getHourCaps, updateHourCaps, getExcedentes } from '../services/hourCapsApi';
import './Global.css';
import './HourCapsPage.css';

/* Os quatro tetos, na ordem em que aparecem no formulário. `dica` é o texto
   curto embaixo do campo — em linguagem de direção, não de sistema. */
const CAMPOS = [
  {
    chave: 'tetoColegio',
    label: 'Efetivo do colégio',
    dica: 'Militar lotado no CMCB: todos os registros dele entram aqui, seja qual for a função.',
  },
  {
    chave: 'tetoItinerante',
    label: 'Monitor Itinerante',
    dica: 'Registro cuja função é itinerante (quem não é do efetivo).',
  },
  {
    chave: 'tetoDemais',
    label: 'Demais funções',
    dica: 'Instrutores e outras funções que não são itinerante. Civis contam aqui.',
  },
  {
    chave: 'tetoGeral',
    label: 'Teto geral por militar',
    dica: 'O que a CGP aceita no ciclo para uma pessoa, somando todas as categorias.',
  },
];

const CATEGORIA_LABEL = {
  colegio: 'Colégio',
  itinerante: 'Itinerante',
  demais: 'Demais',
};

const pad2 = (n) => String(n).padStart(2, '0');
const isoDe = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

/**
 * Ciclo de pagamento: dia 16 ao dia 15 do mês seguinte. `deslocamento` 0 é o
 * ciclo em que `hoje` cai; -1 é o anterior. Calculado no cliente só para
 * montar o seletor — quem decide o ciclo de cada registro é o backend.
 */
const cicloDe = (hoje, deslocamento = 0) => {
  // Mês de referência = mês em que o ciclo COMEÇA.
  let ano = hoje.getFullYear();
  let mes = hoje.getMonth();
  if (hoje.getDate() < 16) mes -= 1;
  mes += deslocamento;
  const inicio = new Date(ano, mes, 16);
  const fim = new Date(ano, mes + 1, 15);
  return {
    inicio: isoDe(inicio.getFullYear(), inicio.getMonth(), 16),
    fim: isoDe(fim.getFullYear(), fim.getMonth(), 15),
  };
};

const formatDateBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/* Mesma faixa do DTO do backend (1..80): abaixo de 1 o teto some, acima de
   80 não existe ciclo de hora-aula que caiba. */
const TETO_MIN = 1;
const TETO_MAX = 80;

const tetoValido = (v) => {
  const n = Number(v);
  return v !== '' && Number.isInteger(n) && n >= TETO_MIN && n <= TETO_MAX;
};

const formatHoras = (h) => {
  const n = Number(h) || 0;
  return Number.isInteger(n) ? `${n}h` : `${n.toFixed(2).replace('.', ',')}h`;
};

/**
 * Tela admin dos tetos de horas por ciclo. Quem passa do teto não some: a
 * hora excedente fica retida no próprio registro (saída real preservada) e a
 * CGP recebe só o pagável. Subir o teto devolve as horas sem intervenção.
 */
const HourCapsPage = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    tetoColegio: '', tetoItinerante: '', tetoDemais: '', tetoGeral: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alternando, setAlternando] = useState(false);
  const tetosAtivos = config ? config.ativo !== false : true;

  const [cicloSel, setCicloSel] = useState('atual');
  const [excedentes, setExcedentes] = useState([]);
  const [loadingExc, setLoadingExc] = useState(false);

  const ciclo = cicloDe(new Date(), cicloSel === 'anterior' ? -1 : 0);

  const carregarExcedentes = useCallback(async (inicio, fim) => {
    setLoadingExc(true);
    try {
      // O backend devolve todo mundo com registro no período (inclusive quem
      // não teve nada retido); a seção é dos excedentes, então filtra aqui.
      const resp = await getExcedentes(inicio, fim);
      const militares = Array.isArray(resp?.militares) ? resp.militares : [];
      setExcedentes(
        militares.filter(
          (m) => Number(m.horasExcedentes) > 0 || Number(m.registrosAjustados) > 0,
        ),
      );
    } catch (error) {
      toast.error('Erro ao carregar os excedentes do ciclo');
      setExcedentes([]);
    } finally {
      setLoadingExc(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await getHourCaps();
        setConfig(cfg);
        setForm({
          tetoColegio: cfg.tetoColegio ?? '',
          tetoItinerante: cfg.tetoItinerante ?? '',
          tetoDemais: cfg.tetoDemais ?? '',
          tetoGeral: cfg.tetoGeral ?? '',
        });
      } catch (error) {
        toast.error('Erro ao carregar os tetos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    carregarExcedentes(ciclo.inicio, ciclo.fim);
  }, [carregarExcedentes, ciclo.inicio, ciclo.fim]);

  const handleSave = async () => {
    const invalido = CAMPOS.find((c) => !tetoValido(form[c.chave]));
    if (invalido) {
      toast.error(`${invalido.label}: informe um número inteiro de horas entre ${TETO_MIN} e ${TETO_MAX}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      CAMPOS.forEach((c) => { payload[c.chave] = Number(form[c.chave]); });
      const { config: novo, resumo } = await updateHourCaps(payload);
      setConfig(novo);
      if (resumo?.erro) {
        // Os tetos valeram; só a varredura ficou pela metade.
        toast.error(resumo.erro, { duration: 10000 });
        carregarExcedentes(ciclo.inicio, ciclo.fim);
        return;
      }
      const ajustados = resumo?.ajustados ?? 0;
      const restaurados = resumo?.restaurados ?? 0;
      toast.success(
        `Tetos salvos. ${ajustados} ${ajustados === 1 ? 'registro ajustado' : 'registros ajustados'}, ` +
          `${restaurados} ${restaurados === 1 ? 'restaurado' : 'restaurados'}.`,
        { duration: 7000 },
      );
      carregarExcedentes(ciclo.inicio, ciclo.fim);
    } catch (error) {
      const m = error.response?.data?.message;
      toast.error('Erro ao salvar: ' + (Array.isArray(m) ? m.join(' ') : m || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner" />
          </div>
        </div>
      </>
    );
  }

  const totalRetido = excedentes.reduce((s, e) => s + (Number(e.horasExcedentes) || 0), 0);

  /**
   * Interruptor geral. Desligar devolve na hora tudo o que estava retido
   * (ciclo atual e anterior) e a CGP passa a receber todas as horas; religar
   * refaz o corte. Os números dos tetos ficam guardados nos dois casos.
   */
  const handleAlternar = async () => {
    const desligar = tetosAtivos;
    const pergunta = desligar
      ? 'Desligar os tetos? Todas as horas retidas serão devolvidas e a CGP passará a receber ' +
        'todas as horas, mesmo acima do teto. Os valores dos tetos ficam guardados para religar.'
      : 'Religar os tetos? O que passar do teto no ciclo atual e no anterior volta a ficar retido.';
    if (!window.confirm(pergunta)) return;
    setAlternando(true);
    try {
      const { config: novo, resumo } = await updateHourCaps({ ativo: !desligar });
      setConfig(novo);
      if (resumo?.erro) {
        toast.error(resumo.erro, { duration: 10000 });
      } else {
        const ajustados = resumo?.ajustados ?? 0;
        const restaurados = resumo?.restaurados ?? 0;
        toast.success(
          (desligar ? 'Tetos desligados. ' : 'Tetos religados. ') +
            `${restaurados} ${restaurados === 1 ? 'registro restaurado' : 'registros restaurados'}, ` +
            `${ajustados} ${ajustados === 1 ? 'ajustado' : 'ajustados'}.`,
          { duration: 8000 },
        );
      }
      carregarExcedentes(ciclo.inicio, ciclo.fim);
    } catch (error) {
      const m = error.response?.data?.message;
      toast.error('Erro ao alterar o interruptor: ' + (Array.isArray(m) ? m.join(' ') : m || error.message));
    } finally {
      setAlternando(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1>Limites de horas</h1>
            <p className="subtitle">
              Teto por categoria no ciclo de pagamento e horas retidas acima dele
            </p>
          </div>
        </div>

        <div className="info-card info-card-blue">
          <div className="info-card-body">
            <p>
              <strong>Como funciona:</strong> cada registro de ponto cai numa categoria pela
              função. Quem é do <strong>efetivo do colégio</strong> conta sempre como colégio;
              para os demais, vale a função do registro — <strong>itinerante</strong> ou{' '}
              <strong>demais funções</strong>. O ciclo de pagamento vai do dia 16 ao dia 15 do
              mês seguinte, e cada militar tem um teto de horas por categoria e um teto geral.
            </p>
            <p>
              O que passa do teto <strong>não desaparece</strong>: o registro continua com o
              horário batido guardado como saída real, mas a parte que excede fica retida — o
              registro passa a valer as horas que ainda cabiam (ou 0h, se o teto já estava
              fechado). A CGP recebe só o pagável. Subir o teto devolve as horas retidas
              sozinho; baixar retém mais. Um teto individual no cadastro do militar substitui o
              da categoria dele e também limita o total dele no ciclo.
            </p>
          </div>
        </div>

        {config ? (
          <div className={`info-card hc-switch${tetosAtivos ? ' hc-switch-on' : ' hc-switch-off'}`}>
            <div className="info-card-body hc-switch-body">
              <div className="hc-switch-texto">
                <strong className="hc-switch-estado">
                  {tetosAtivos ? 'Tetos ligados' : 'Tetos DESLIGADOS'}
                </strong>
                <span className="hc-switch-desc">
                  {tetosAtivos
                    ? 'O que passa do teto fica retido no registro e não vai para a CGP.'
                    : 'Nada é retido: a CGP recebe todas as horas de cada registro, mesmo acima do teto. ' +
                      'Os valores abaixo ficam guardados e voltam a valer quando religar.'}
                  {config.updatedByName
                    ? ` Última alteração por ${config.updatedByName} em ${formatDateTime(config.updatedAt)}.`
                    : ''}
                </span>
              </div>
              <button
                type="button"
                className={tetosAtivos ? 'btn-secondary hc-switch-btn' : 'btn-primary hc-switch-btn'}
                onClick={handleAlternar}
                disabled={alternando || saving}
              >
                {alternando
                  ? 'Refazendo a conta...'
                  : tetosAtivos
                    ? 'Desligar tetos (enviar todas as horas)'
                    : 'Religar tetos'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="info-card hc-card">
          <div className="info-card-body">
            <h3 className="hc-titulo">
              <RulerHorizontalIcon width={18} height={18} /> Tetos por ciclo (horas inteiras)
              {!tetosAtivos ? <span className="hc-tag-off">desligados</span> : null}
            </h3>
            <div className="hc-grid">
              {CAMPOS.map((c) => (
                <div className="form-group" key={c.chave}>
                  <label htmlFor={`hc-${c.chave}`}>{c.label}</label>
                  <input
                    id={`hc-${c.chave}`}
                    type="number"
                    min={TETO_MIN}
                    max={TETO_MAX}
                    step="1"
                    value={form[c.chave]}
                    onChange={(e) => setForm({ ...form, [c.chave]: e.target.value })}
                  />
                  <small className={`form-hint${tetoValido(form[c.chave]) ? '' : ' hc-warn'}`}>
                    {tetoValido(form[c.chave]) ? c.dica : `Informe um número inteiro entre ${TETO_MIN} e ${TETO_MAX}.`}
                  </small>
                </div>
              ))}
            </div>

            <div className="hc-actions">
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando e realocando...' : 'Salvar'}
              </button>
              <span className="hc-updated">
                {config?.updatedByName
                  ? `Atualizado por ${config.updatedByName} em ${formatDateTime(config.updatedAt)}`
                  : 'Valores padrão'}
              </span>
            </div>
            <small className="form-hint hc-aviso-salvar">
              Ao salvar, o sistema refaz a conta de todo mundo desde o início do ciclo anterior
              e avisa quantos registros foram ajustados ou restaurados.
            </small>
          </div>
        </div>

        <div className="hc-secao">
          <div className="hc-secao-header">
            <div>
              <h2>Excedentes retidos</h2>
              <p className="hc-secao-sub">
                Ciclo de {formatDateBR(ciclo.inicio)} a {formatDateBR(ciclo.fim)}
                {excedentes.length > 0 && ` · ${formatHoras(totalRetido)} retidas no total`}
              </p>
            </div>
            <div className="hc-secao-tools">
              <div className="hc-seg" role="group" aria-label="Ciclo">
                <button
                  type="button"
                  className={`hc-seg-btn${cicloSel === 'atual' ? ' is-on' : ''}`}
                  aria-pressed={cicloSel === 'atual'}
                  onClick={() => setCicloSel('atual')}
                >
                  Ciclo atual
                </button>
                <button
                  type="button"
                  className={`hc-seg-btn${cicloSel === 'anterior' ? ' is-on' : ''}`}
                  aria-pressed={cicloSel === 'anterior'}
                  onClick={() => setCicloSel('anterior')}
                >
                  Ciclo anterior
                </button>
              </div>
              <button
                type="button"
                className="btn-secondary hc-refresh"
                onClick={() => carregarExcedentes(ciclo.inicio, ciclo.fim)}
                disabled={loadingExc}
                title="Recarregar"
              >
                <UpdateIcon width={14} height={14} /> Atualizar
              </button>
            </div>
          </div>

          <div className="table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Militar</th>
                  <th>Categoria</th>
                  <th className="text-center col-num">Teto</th>
                  <th className="text-center col-num">Horas pagáveis</th>
                  <th className="text-center col-num">Excedentes</th>
                  <th className="text-center col-num">Registros ajustados</th>
                </tr>
              </thead>
              <tbody>
                {loadingExc ? (
                  <tr>
                    <td colSpan="6" className="text-center empty-state">
                      Carregando...
                    </td>
                  </tr>
                ) : excedentes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center empty-state">
                      <strong>Ninguém passou do teto neste ciclo</strong>
                      Todas as horas registradas cabem nos limites — nada foi retido.
                    </td>
                  </tr>
                ) : (
                  excedentes.map((e) => (
                    <tr key={`${e.userId}-${e.categoria}`}>
                      <td className="cell-primary">
                        {e.posto ? <span className="hc-posto">{e.posto}</span> : null}
                        {e.nome}
                        {e.matricula && <span className="cell-sub">{e.matricula}</span>}
                      </td>
                      <td>
                        <span className={`hc-cat hc-cat-${e.categoria}`}>
                          {CATEGORIA_LABEL[e.categoria] || e.categoria}
                        </span>
                      </td>
                      <td className="text-center col-num">{formatHoras(e.teto)}</td>
                      <td className="text-center col-num">{formatHoras(e.horasPagaveis)}</td>
                      <td className="text-center col-num">
                        <span className="hc-excedente">{formatHoras(e.horasExcedentes)}</span>
                      </td>
                      <td className="text-center col-num">{e.registrosAjustados}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default HourCapsPage;
